import { Grid, SelectChangeEvent } from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  CERTIFICATE_NAME,
  DEFAULT_PAGE_NO,
  DEFAULT_PAGE_SIZE,
  PAST_COURSE_TYPE,
} from "utils/constants/constant";
import { Order, OrderByOptions } from "utils/enums/sorting";
import {
  allowSearch,
  base64ToArrayBuffer,
  getPagePayloadValuesWithId,
  getPagePermissions,
  hideLoaderForBatchApiCall,
  showLoader,
  showLoaderForBatchApiCall,
  toggleFilter,
} from "utils/helper";
import { useHistory } from "react-router-dom";
import {
  IPastCourses,
  IStudentDashboardListMeta,
  IStudentDashboardPagePayloadValues,
  IUpcomingCourseFilter,
} from "utils/interfaces/student-dashboard";
import {
  DownloadCertificateSuccessPayload,
  PastCourseListSuccessPayload,
} from "store/studentDashboard/types";
import { useSelector } from "react-redux";
import { IRoleModulePermission } from "store/role/types";
import { AppPages } from "utils/enums/app-pages";
import saveAs from "file-saver";
import { SCOBlendedCourse } from "utils/enums/scheduled-offering";
import StudentCourseTable from "components/StudentCoursesTable";
import StudentCoursesFilter from "../StudentCoursesFilter";

const PastCoursesList: React.FC<any> = (props) => {
  const history = useHistory();
  const [page, setPage] = useState(DEFAULT_PAGE_SIZE);
  const [pageNo, setPageNo] = useState<number>(DEFAULT_PAGE_NO);
  const user = useSelector((state: any) => state?.auth?.user);
  const [order, setOrder] = useState<Order>(OrderByOptions.Asc);
  const [orderBy, setOrderBy] = useState<keyof IPastCourses>("courseName");
  const [filteredData, setFilteredData] = useState<IUpcomingCourseFilter>();
  const [pagePermission, setPagePermission] = useState<IRoleModulePermission>();
  const [pastCoursesList, setPastCoursesList] = useState<IPastCourses[]>();
  const [filterBox, setFilterBox] = useState(false);
  const [pastCoursesListMeta, setPastCoursesListMeta] =
    useState<IStudentDashboardListMeta>();
  const [searchText, setSearchText] = useState<string>("");

  const handleSearch = (event: any) => {
    setSearchText(event?.target?.value);
    setFilteredData((prevState: any) => ({
      ...prevState,
      name: event?.target?.value,
    }));
  };

  const handleChange = (event: SelectChangeEvent) => {
    setPage(event?.target?.value as string);
    setPageNo(1);
  };

  const onPageChange = (e: any, value: number) => {
    setPageNo(value);
  };

  const handleRequestSort = React.useCallback(
    (event: React.MouseEvent<unknown>, newOrderBy: keyof IPastCourses) => {
      const isAsc = orderBy === newOrderBy && order === OrderByOptions.Asc;
      const toggledOrder = isAsc ? OrderByOptions.Desc : OrderByOptions.Asc;
      setOrder(toggledOrder);
      setOrderBy(newOrderBy);
    },
    [order, orderBy]
  );

  const onPastCoursesListSuccess = (response: PastCourseListSuccessPayload) => {
    setPastCoursesList(response?.pastCourseList);
    setPastCoursesListMeta(response?.pastCourseListPagination);
    hideLoaderForBatchApiCall();
  };

  const getPastCoursesList = (filterData?: IUpcomingCourseFilter) => {
    const { pastCourseListRequest } = props;
    if (pastCourseListRequest) {
      showLoaderForBatchApiCall();
      const values: IStudentDashboardPagePayloadValues =
        getPagePayloadValuesWithId(
          user?.studentId,
          orderBy,
          order,
          pageNo,
          page
        );
      if (filterData?.name) {
        values.name = filterData?.name;
      }
      if (filterData?.term) {
        values.term = filterData?.term;
      }
      if (filterData?.locationName) {
        values.locationName = filterData?.locationName;
      }
      if (
        filterData?.isBlendedCourse === SCOBlendedCourse.No ||
        filterData?.isBlendedCourse === SCOBlendedCourse.Yes
      ) {
        values.isBlendedCourse = filterData?.isBlendedCourse;
      }
      if (filterData) {
        setFilteredData(filterData);
      } else {
        setFilteredData(undefined);
      }
      const payload = {
        values,
        callback: onPastCoursesListSuccess,
      };
      pastCourseListRequest(payload);
    }
  };

  const handleSearchKeyDown = (e: any) => {
    if (allowSearch(e)) {
      setPageNo(1);
      getPastCoursesList(filteredData);
    }
  };

  useEffect(() => {
    const permission = getPagePermissions(
      user?.permissions,
      AppPages.StudentDashboard,
      "student"
    );
    setPagePermission(permission);
    if (permission?.view) {
      if (filteredData) {
        getPastCoursesList(filteredData);
      } else {
        getPastCoursesList();
      }
    } else {
      history.goBack();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, order, orderBy, page]);

  const handleFilter = () => {
    setFilterBox(true);
    toggleFilter();
  };

  const handleFilterApplyClick = (formData: IUpcomingCourseFilter) => {
    setFilteredData(formData);
    setSearchText(formData?.name);
    if (pageNo === DEFAULT_PAGE_NO) {
      getPastCoursesList(formData);
    } else {
      setPageNo(1);
    }
    setFilterBox(false);
    toggleFilter();
  };

  const clearSearchText = () => {
    setSearchText("");
    setFilteredData((prevFilteredData: any) => ({
      ...prevFilteredData,
      name: "",
    }));
    getPastCoursesList({
      name: "",
      locationName: filteredData?.locationName ? filteredData?.locationName : 0,
      term: filteredData?.term ? filteredData?.term : 0,
      isBlendedCourse: filteredData?.isBlendedCourse
        ? filteredData?.isBlendedCourse
        : null,
    });
  };

  const clearFilter = () => {
    setSearchText("");
    setFilteredData((prevFilteredData: any) => ({
      ...prevFilteredData,
      name: "",
    }));
  };

  const onDownloadCertificateSuccess = (
    response: DownloadCertificateSuccessPayload
  ) => {
    const item = base64ToArrayBuffer(response.item);
    const file = new Blob([item], {
      type: "application/pdf",
    });

    saveAs(file, CERTIFICATE_NAME);
  };

  const onDownloadCertificate = (id: number) => {
    const { downloadCertificateRequest } = props;

    if (downloadCertificateRequest) {
      showLoader();

      const payload = {
        values: { id },
        callback: onDownloadCertificateSuccess,
      };

      downloadCertificateRequest(payload);
    }
  };

  return (
    <Grid item xs={12}>
      <StudentCourseTable
        coursesList={pastCoursesList}
        pagePermission={pagePermission}
        courseListMeta={pastCoursesListMeta}
        handleRequestSort={handleRequestSort}
        order={order}
        orderBy={orderBy}
        handleChange={handleChange}
        onPageChange={onPageChange}
        page={page}
        pageNo={pageNo}
        courseListType={PAST_COURSE_TYPE}
        handleFilter={handleFilter}
        searchText={searchText}
        handleSearch={handleSearch}
        handleSearchKeyDown={handleSearchKeyDown}
        handleClearSearch={clearSearchText}
        downloadCertificate={onDownloadCertificate}
      />
      {filterBox && (
        <StudentCoursesFilter
          filterBox={filterBox}
          setFilterBox={setFilterBox}
          handleApplyClick={handleFilterApplyClick}
          filteredData={filteredData}
          pageNo={pageNo}
          setPageNo={setPageNo}
          setFilteredData={setFilteredData}
          getUpcomingCoursesList={getPastCoursesList}
          clearSearchText={clearFilter}
        />
      )}
    </Grid>
  );
};

export default PastCoursesList;
